import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  useTheme,
  Card,
  CardMedia,
  Chip,
  Grid,
  CircularProgress,
  Stack,
  Button,
  Divider,
} from "@mui/material";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { API_BASE_URL } from "../../config/api";
import StarIcon from "@mui/icons-material/Star";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

const PartnerProductDetails = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        
        if (!token) {
          throw new Error("No authentication token found");
        }

        const response = await fetch(`${API_BASE_URL}/company/products`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch products");
        }

        const data = await response.json();
        if (data.subCode === 200 && data.items) {
          const foundProduct = data.items.find((p) => p._id === productId);
          if (foundProduct) {
            setProduct(foundProduct);
          } else {
            setError("Product not found");
          }
        } else {
          setError("Failed to load product: " + (data.message || "Unknown error"));
        }
      } catch (err) {
        setError(err.message);
        console.error("Product fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchProduct();
    }
  }, [productId]);

  // Render HTML content safely
  const renderHtml = (html) => {
    if (!html) return null;
    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  };

  if (loading) {
    return (
      <Box m="20px" display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !product) {
    return (
      <Box m="20px">
        <Header title="PRODUCT DETAILS" subtitle="View product information" />
        <Box color="error.main" p={2} mb={2}>
          Error: {error || "Product not found"}
        </Box>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/partner-products")}
        >
          Back to Products
        </Button>
      </Box>
    );
  }

  return (
    <Box m="20px">
      {/* Header with Back Button */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Header title="PRODUCT DETAILS" subtitle="Complete product information" />
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/partner-products")}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
      </Box>

      {/* Action Buttons */}
      <Box display="flex" gap={2} mb={3}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<EditIcon />}
          onClick={() => {
            // Placeholder for edit functionality
            console.log("Edit product:", product._id);
          }}
          sx={{
            backgroundColor: colors.blueAccent[700],
            "&:hover": {
              backgroundColor: colors.blueAccent[600],
            },
          }}
        >
          Edit Product
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => {
            // Placeholder for delete functionality
            console.log("Delete product:", product._id);
          }}
        >
          Delete Product
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Main Info */}
        <Grid item xs={12} md={8}>
          {/* Product Image */}
          {product.image && (
            <Card sx={{ mb: 3, backgroundColor: colors.primary[400] }}>
              <CardMedia
                component="img"
                height="400"
                image={
                  product.image.startsWith("http")
                    ? product.image
                    : `https://app.airportparkwithus.co.uk/images/upload/${product.image}`
                }
                alt={product.company_name}
                sx={{
                  objectFit: "contain",
                  backgroundColor: colors.primary[500],
                  p: 2,
                }}
                onError={(e) => {
                  e.target.src = "/assets/user.png";
                }}
              />
            </Card>
          )}

          {/* Company Name & Rating */}
          <Card sx={{ mb: 3, backgroundColor: colors.primary[400], p: 3 }}>
            <Typography variant="h4" fontWeight="bold" color={colors.grey[100]} mb={2}>
              {product.company_name}
            </Typography>
            {product.company_rating_show && (
              <Box display="flex" alignItems="center" mb={2}>
                <StarIcon sx={{ color: colors.greenAccent[500], fontSize: "24px", mr: 1 }} />
                <Typography variant="h6" color={colors.grey[100]}>
                  {parseFloat(product.company_rating_show).toFixed(1)} / 5.0
                </Typography>
                <Typography variant="body2" color={colors.grey[400]} ml={1}>
                  ({product.company_rating} reviews)
                </Typography>
              </Box>
            )}
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              <Chip
                label={product.isactive ? "Active" : "Inactive"}
                icon={product.isactive ? <CheckCircleIcon /> : <CancelIcon />}
                color={product.isactive ? "success" : "default"}
              />
              {product.featured && (
                <Chip
                  label="Featured"
                  sx={{
                    backgroundColor: colors.greenAccent[600],
                    color: colors.grey[100],
                  }}
                />
              )}
              {product.sku && (
                <Chip
                  label={`SKU: ${product.sku}`}
                  sx={{
                    backgroundColor: colors.blueAccent[700],
                    color: colors.grey[100],
                  }}
                />
              )}
            </Stack>
          </Card>

          {/* Description */}
          {product.description && (
            <Card sx={{ mb: 3, backgroundColor: colors.primary[400], p: 3 }}>
              <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={2}>
                Description
              </Typography>
              <Divider sx={{ mb: 2, backgroundColor: colors.primary[500] }} />
              <Typography
                variant="body1"
                color={colors.grey[300]}
                sx={{ whiteSpace: "pre-wrap" }}
              >
                {renderHtml(product.description)}
              </Typography>
            </Card>
          )}

          {/* Company Info */}
          {product.company_info && (
            <Card sx={{ mb: 3, backgroundColor: colors.primary[400], p: 3 }}>
              <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={2}>
                Company Information
              </Typography>
              <Divider sx={{ mb: 2, backgroundColor: colors.primary[500] }} />
              <Typography
                variant="body1"
                color={colors.grey[300]}
                sx={{ whiteSpace: "pre-wrap" }}
              >
                {renderHtml(product.company_info)}
              </Typography>
            </Card>
          )}

          {/* On Arrival */}
          {product.on_arrival && (
            <Card sx={{ mb: 3, backgroundColor: colors.primary[400], p: 3 }}>
              <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={2}>
                On Arrival Instructions
              </Typography>
              <Divider sx={{ mb: 2, backgroundColor: colors.primary[500] }} />
              <Typography
                variant="body1"
                color={colors.grey[300]}
                sx={{ whiteSpace: "pre-wrap" }}
              >
                {renderHtml(product.on_arrival)}
              </Typography>
            </Card>
          )}

          {/* On Return */}
          {product.on_return && (
            <Card sx={{ mb: 3, backgroundColor: colors.primary[400], p: 3 }}>
              <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={2}>
                On Return Instructions
              </Typography>
              <Divider sx={{ mb: 2, backgroundColor: colors.primary[500] }} />
              <Typography
                variant="body1"
                color={colors.grey[300]}
                sx={{ whiteSpace: "pre-wrap" }}
              >
                {renderHtml(product.on_return)}
              </Typography>
            </Card>
          )}

          {/* Terms */}
          {product.term && (
            <Card sx={{ mb: 3, backgroundColor: colors.primary[400], p: 3 }}>
              <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={2}>
                Terms & Conditions
              </Typography>
              <Divider sx={{ mb: 2, backgroundColor: colors.primary[500] }} />
              <Typography variant="body1" color={colors.grey[300]}>
                {product.term}
              </Typography>
            </Card>
          )}
        </Grid>

        {/* Right Column - Details */}
        <Grid item xs={12} md={4}>
          {/* Price Card */}
          {product.total_amount && (
            <Card sx={{ mb: 3, backgroundColor: colors.primary[400], p: 3 }}>
              <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={1}>
                Price
              </Typography>
              <Typography
                variant="h3"
                fontWeight="bold"
                color={colors.greenAccent[500]}
                mb={2}
              >
                £{parseFloat(product.total_amount).toFixed(2)}
              </Typography>
              {product.discount_code && (
                <Typography variant="body2" color={colors.grey[400]}>
                  Discount Code: {product.discount_code}
                </Typography>
              )}
            </Card>
          )}

          {/* Location & Terminals */}
          <Card sx={{ mb: 3, backgroundColor: colors.primary[400], p: 3 }}>
            <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={2}>
              Location
            </Typography>
            <Divider sx={{ mb: 2, backgroundColor: colors.primary[500] }} />
            <Box display="flex" alignItems="center" mb={2}>
              <LocationOnIcon
                sx={{ color: colors.blueAccent[500], fontSize: "20px", mr: 1 }}
              />
              <Typography variant="body1" color={colors.grey[100]} fontWeight="bold">
                {product.airport_name || "N/A"}
              </Typography>
            </Box>
            {product.terminal && product.terminal.length > 0 && (
              <Box>
                <Typography variant="body2" color={colors.grey[300]} mb={1}>
                  Terminals:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {product.terminal.map((term, idx) => (
                    <Chip
                      key={idx}
                      label={term.name}
                      sx={{
                        backgroundColor: colors.blueAccent[700],
                        color: colors.grey[100],
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Card>

          {/* Contact Information */}
          <Card sx={{ mb: 3, backgroundColor: colors.primary[400], p: 3 }}>
            <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={2}>
              Contact Information
            </Typography>
            <Divider sx={{ mb: 2, backgroundColor: colors.primary[500] }} />
            {product.company_email && (
              <Box display="flex" alignItems="center" mb={2}>
                <EmailIcon
                  sx={{ color: colors.greenAccent[500], fontSize: "20px", mr: 1 }}
                />
                <Typography variant="body1" color={colors.grey[300]}>
                  {product.company_email}
                </Typography>
              </Box>
            )}
            {product.phone && (
              <Box display="flex" alignItems="center" mb={2}>
                <PhoneIcon
                  sx={{ color: colors.greenAccent[500], fontSize: "20px", mr: 1 }}
                />
                <Typography variant="body1" color={colors.grey[300]}>
                  {product.phone}
                </Typography>
              </Box>
            )}
            {product.cemail && (
              <Box display="flex" alignItems="center" mb={2}>
                <EmailIcon
                  sx={{ color: colors.greenAccent[500], fontSize: "20px", mr: 1 }}
                />
                <Typography variant="body2" color={colors.grey[400]}>
                  Contact Email: {product.cemail}
                </Typography>
              </Box>
            )}
            {product.cnum && (
              <Box display="flex" alignItems="center">
                <PhoneIcon
                  sx={{ color: colors.greenAccent[500], fontSize: "20px", mr: 1 }}
                />
                <Typography variant="body2" color={colors.grey[400]}>
                  Contact Number: {product.cnum}
                </Typography>
              </Box>
            )}
          </Card>

          {/* Booking Details */}
          <Card sx={{ mb: 3, backgroundColor: colors.primary[400], p: 3 }}>
            <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={2}>
              Booking Details
            </Typography>
            <Divider sx={{ mb: 2, backgroundColor: colors.primary[500] }} />
            <Grid container spacing={2}>
              {product.start_time && (
                <Grid item xs={6}>
                  <Typography variant="body2" color={colors.grey[400]}>
                    Start Time
                  </Typography>
                  <Typography variant="body1" color={colors.grey[100]}>
                    {product.start_time}
                  </Typography>
                </Grid>
              )}
              {product.end_time && (
                <Grid item xs={6}>
                  <Typography variant="body2" color={colors.grey[400]}>
                    End Time
                  </Typography>
                  <Typography variant="body1" color={colors.grey[100]}>
                    {product.end_time}
                  </Typography>
                </Grid>
              )}
              {product.sms_confirmation && (
                <Grid item xs={6}>
                  <Typography variant="body2" color={colors.grey[400]}>
                    SMS Confirmation
                  </Typography>
                  <Typography variant="body1" color={colors.grey[100]}>
                    £{parseFloat(product.sms_confirmation).toFixed(2)}
                  </Typography>
                </Grid>
              )}
              {product.cancellation_cover && (
                <Grid item xs={6}>
                  <Typography variant="body2" color={colors.grey[400]}>
                    Cancellation Cover
                  </Typography>
                  <Typography variant="body1" color={colors.grey[100]}>
                    £{parseFloat(product.cancellation_cover).toFixed(2)}
                  </Typography>
                </Grid>
              )}
              {product.booking_fee && (
                <Grid item xs={6}>
                  <Typography variant="body2" color={colors.grey[400]}>
                    Booking Fee
                  </Typography>
                  <Typography variant="body1" color={colors.grey[100]}>
                    £{parseFloat(product.booking_fee).toFixed(2)}
                  </Typography>
                </Grid>
              )}
              {product.short_hours && (
                <Grid item xs={6}>
                  <Typography variant="body2" color={colors.grey[400]}>
                    Short Hours
                  </Typography>
                  <Typography variant="body1" color={colors.grey[100]}>
                    {product.short_hours} hours
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Card>

          {/* Security Features */}
          <Card sx={{ mb: 3, backgroundColor: colors.primary[400], p: 3 }}>
            <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={2}>
              Security Features
            </Typography>
            <Divider sx={{ mb: 2, backgroundColor: colors.primary[500] }} />
            <Stack spacing={1}>
              {[
                { key: "Hr_Patrols", label: "HR Patrols" },
                { key: "cctv", label: "CCTV" },
                { key: "fencing", label: "Fencing" },
                { key: "keep_your_keys", label: "Keep Your Keys" },
                { key: "security_lighting", label: "Security Lighting" },
                { key: "secure_barrier", label: "Secure Barrier" },
                { key: "patrolled", label: "Patrolled" },
                { key: "park_mark", label: "Park Mark" },
                { key: "disability_friendly", label: "Disability Friendly" },
                { key: "bpa_member", label: "BPA Member" },
                { key: "buy_with_confidence", label: "Buy With Confidence" },
              ].map((feature) => (
                <Box key={feature.key} display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color={colors.grey[300]}>
                    {feature.label}
                  </Typography>
                  {product[feature.key] ? (
                    <CheckCircleIcon sx={{ color: colors.greenAccent[500] }} />
                  ) : (
                    <CancelIcon sx={{ color: colors.grey[600] }} />
                  )}
                </Box>
              ))}
            </Stack>
          </Card>

          {/* Additional Info */}
          <Card sx={{ backgroundColor: colors.primary[400], p: 3 }}>
            <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={2}>
              Additional Information
            </Typography>
            <Divider sx={{ mb: 2, backgroundColor: colors.primary[500] }} />
            <Stack spacing={1.5}>
              {product.company_id && (
                <Box>
                  <Typography variant="body2" color={colors.grey[400]}>
                    Company ID
                  </Typography>
                  <Typography variant="body1" color={colors.grey[100]}>
                    {product.company_id}
                  </Typography>
                </Box>
              )}
              {product.commision && (
                <Box>
                  <Typography variant="body2" color={colors.grey[400]}>
                    Commission
                  </Typography>
                  <Typography variant="body1" color={colors.grey[100]}>
                    {product.commision}%
                  </Typography>
                </Box>
              )}
              {product.valeting && (
                <Box>
                  <Typography variant="body2" color={colors.grey[400]}>
                    Valeting
                  </Typography>
                  <Typography variant="body1" color={colors.grey[100]}>
                    £{parseFloat(product.valeting).toFixed(2)}
                  </Typography>
                </Box>
              )}
              {product.sequence !== undefined && (
                <Box>
                  <Typography variant="body2" color={colors.grey[400]}>
                    Sequence
                  </Typography>
                  <Typography variant="body1" color={colors.grey[100]}>
                    {product.sequence}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PartnerProductDetails;


